import {
  UserCircleIcon,
  MailIcon,
  PhoneIcon,
  IdentificationIcon,
  OfficeBuildingIcon,
  UserIcon,
  LockClosedIcon,
  ArrowLeftIcon,
  PencilIcon,
} from '@heroicons/react/outline';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectCurrentUser } from '../../Features/auth/authSlice';

function UserProfileView() {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);

  const editUserPassword = () => {
    // Add password change functionality here
  };

  const editUser = () => {
    // Add edit profile functionality here
  };

  const back = () => navigate(-1);

  const getGender = (gender) => {
    if (gender === 1 || gender === '1') {
      return 'Male';
    }

    if (gender === 2 || gender === '2') {
      return 'Female';
    }

    return 'Not specified';
  };

  const profileItems = [
    {
      label: 'Full Name',
      value:
        `${user?.fname || ''} ${user?.lname || ''}`.trim() || '—',
      icon: UserIcon,
    },
    {
      label: 'Email Address',
      value: user?.email || '—',
      icon: MailIcon,
    },
    {
      label: 'Phone Number',
      value: user?.phone || '—',
      icon: PhoneIcon,
    },
    {
      label: 'User ID',
      value: user?.id || '—',
      icon: IdentificationIcon,
    },
    {
      label: 'Business / Company',
      value: user?.business_name || '—',
      icon: OfficeBuildingIcon,
    },
    {
      label: 'Gender',
      value: getGender(user?.gender),
      icon: UserCircleIcon,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 text-gray-800 dark:text-white transition-colors">

      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <UserCircleIcon className="w-6 h-6 text-[#80b643]" />
              </div>

              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                  User Profile
                </h1>

                <p className="text-sm text-gray-500 dark:text-gray-400">
                  View and manage your account information
                </p>
              </div>
            </div>
          </div>

          {/* Back Button */}
          <button
            type="button"
            onClick={back}
            className="
              inline-flex items-center justify-center gap-2
              px-4 py-2.5
              rounded-xl
              border border-gray-200 dark:border-gray-800
              bg-white dark:bg-gray-900
              text-sm font-medium
              text-gray-700 dark:text-gray-300
              hover:border-[#80b643]
              hover:text-[#80b643]
              transition
            "
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Profile Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Profile Summary */}
        <div
          className="
            bg-white dark:bg-gray-900
            border border-gray-200 dark:border-gray-800
            rounded-xl
            shadow-sm
            overflow-hidden
          "
        >
          <div className="p-6">
            <div className="flex flex-col items-center text-center">

              {/* Avatar */}
              <div
                className="
                  w-24 h-24
                  rounded-full
                  bg-[#80b643]/10
                  dark:bg-[#80b643]/20
                  flex items-center justify-center
                  border-4 border-white
                  dark:border-gray-900
                  shadow-sm
                "
              >
                <UserCircleIcon className="w-20 h-20 text-[#80b643]" />
              </div>

              <h2 className="mt-4 text-lg font-semibold text-gray-800 dark:text-white uppercase">
                {user?.fname || ''} {user?.lname || ''}
              </h2>

              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {user?.email || 'No email available'}
              </p>

              {user?.business_name && (
                <div className="mt-4">
                  <span
                    className="
                      inline-flex items-center
                      px-3 py-1.5
                      rounded-full
                      bg-[#80b643]/10
                      dark:bg-[#80b643]/20
                      text-xs font-medium
                      text-[#80b643]
                    "
                  >
                    {user.business_name}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* User ID */}
          <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <IdentificationIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </div>

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  User ID
                </p>

                <p className="text-sm font-medium text-gray-800 dark:text-white break-all">
                  {user?.id || '—'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div
          className="
            lg:col-span-2
            bg-white dark:bg-gray-900
            border border-gray-200 dark:border-gray-800
            rounded-xl
            shadow-sm
            overflow-hidden
          "
        >

          {/* Card Header */}
          <div className="px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-base font-semibold text-gray-800 dark:text-white">
              Account Information
            </h2>

            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Your personal and business account details
            </p>
          </div>

          {/* Information Grid */}
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {profileItems.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.label}
                    className="
                      rounded-xl
                      border border-gray-200 dark:border-gray-800
                      bg-gray-50 dark:bg-gray-950
                      p-4
                      transition
                      hover:border-[#80b643]/50
                    "
                  >
                    <div className="flex items-start gap-3">

                      <div
                        className="
                          w-10 h-10
                          shrink-0
                          rounded-xl
                          bg-[#80b643]/10
                          dark:bg-[#80b643]/20
                          flex items-center justify-center
                        "
                      >
                        <Icon className="w-5 h-5 text-[#80b643]" />
                      </div>

                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {item.label}
                        </p>

                        <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white break-words">
                          {item.value}
                        </p>
                      </div>

                    </div>
                  </div>
                );
              })}

            </div>
          </div>

          {/* Account Actions */}
          <div className="border-t border-gray-200 dark:border-gray-800">
            <div className="px-5 sm:px-6 py-4">

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                    Account Settings
                  </h3>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Update your profile information or account password.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">

                  {/* Edit Profile */}
                  <button
                    type="button"
                    onClick={editUser}
                    className="
                      inline-flex items-center justify-center gap-2
                      px-4 py-2.5
                      rounded-xl
                      border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900
                      text-gray-700 dark:text-gray-300
                      text-sm font-medium
                      hover:border-[#80b643]
                      hover:text-[#80b643]
                      transition
                      w-full sm:w-auto
                    "
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit Profile
                  </button>

                  {/* Change Password */}
                  <button
                    type="button"
                    onClick={editUserPassword}
                    className="
                      inline-flex items-center justify-center gap-2
                      px-4 py-2.5
                      rounded-xl
                      bg-[#80b643]
                      text-white
                      text-sm font-medium
                      shadow-sm
                      hover:bg-[#719f3b]
                      focus:outline-none
                      focus:ring-2
                      focus:ring-[#80b643]/20
                      transition
                      w-full sm:w-auto
                    "
                  >
                    <LockClosedIcon className="w-4 h-4" />
                    Change Password
                  </button>

                </div>

              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfileView;